import {
  ApiChartAttribute,
  ApiChartLayout,
  ApiChartTemplate,
  ApiChartType,
  ApiDimension,
  ApiMetric,
  ApiOperator,
  ApiYAxisChartType,
} from "@incendium/api";
import { Box, Stack } from "@mui/material";
import StandardAnalyticsCard from "features/analytics/components/StandardAnalyticsCard";
import {
  CHART_KEY_DASHBOARD_SLOW_PAGES_CWV_BAR,
  CHART_KEY_DASHBOARD_SLOW_PAGES_TABLE,
  CHART_KEY_DASHBOARD_SLOW_PAGES_TRAFFIC_BAR,
} from "features/analytics/constants/chartKeys";
import { table5Rows } from "features/analytics/constants/constants";

interface ISlowPagesTabProps {
  attributes: ApiChartAttribute[];
}

function SlowPagesTab({ attributes }: ISlowPagesTabProps) {
  return (
    <>
      <Stack
        direction={{ xs: "column", md: "row" }}
        spacing={2}
        sx={{ flexShrink: 0, width: "100%", minWidth: 0 }}
      >
        <Box sx={{ height: { xs: 320, md: 400 }, flex: 1, minWidth: 0 }}>
          <StandardAnalyticsCard
            chartKey={CHART_KEY_DASHBOARD_SLOW_PAGES_CWV_BAR}
            staticAttributes={attributes}
            ignoreAttrModel
            chart={{
              name: "Lowest CWV Scores",
              dimension: [ApiDimension.DIMENSION_LOCATION],
              template: ApiChartTemplate.SNAPSHOT,
              type: ApiChartType.GRAPH,
              attributes: [],
              limit: 10,
              orderBy: `${ApiMetric.METRIC_AVERAGE_CWV_PERFORMANCE_SCORE} ASC`,
              having: [
                {
                  metric: ApiMetric.METRIC_PAGEVIEWS_COUNT,
                  operator: ApiOperator.IS_GREATER_THAN,
                  value: 0,
                },
                {
                  metric: ApiMetric.METRIC_AVERAGE_CWV_PERFORMANCE_SCORE,
                  operator: ApiOperator.IS_GREATER_THAN,
                  value: 0,
                },
              ],
              yAxisKeys: [
                {
                  key: "l",
                  fields: [ApiMetric.METRIC_AVERAGE_CWV_PERFORMANCE_SCORE],
                  chart: {
                    [ApiMetric.METRIC_AVERAGE_CWV_PERFORMANCE_SCORE]:
                      ApiYAxisChartType.BAR,
                  },
                },
              ],
              displayOptions: {
                noFilters: true,
                noAttributionToggle: true,
                layout: ApiChartLayout.VERTICAL,
                noLegend: true,
              },
            }}
          />
        </Box>

        <Box sx={{ height: { xs: 320, md: 400 }, flex: 1, minWidth: 0 }}>
          <StandardAnalyticsCard
            chartKey={CHART_KEY_DASHBOARD_SLOW_PAGES_TRAFFIC_BAR}
            staticAttributes={attributes}
            ignoreAttrModel
            chart={{
              name: "Traffic on Slowest Pages",
              dimension: [ApiDimension.DIMENSION_LOCATION],
              template: ApiChartTemplate.SNAPSHOT,
              type: ApiChartType.GRAPH,
              attributes: [],
              limit: 10,
              orderBy: `${ApiMetric.METRIC_AVERAGE_CWV_PERFORMANCE_SCORE} ASC`,
              having: [
                {
                  metric: ApiMetric.METRIC_PAGEVIEWS_COUNT,
                  operator: ApiOperator.IS_GREATER_THAN,
                  value: 0,
                },
                {
                  metric: ApiMetric.METRIC_AVERAGE_CWV_PERFORMANCE_SCORE,
                  operator: ApiOperator.IS_GREATER_THAN,
                  value: 0,
                },
              ],
              yAxisKeys: [
                {
                  key: "l",
                  fields: [ApiMetric.METRIC_PAGEVIEWS_COUNT],
                  chart: {
                    [ApiMetric.METRIC_PAGEVIEWS_COUNT]: ApiYAxisChartType.BAR,
                  },
                },
              ],
              displayOptions: {
                noFilters: true,
                noAttributionToggle: true,
                layout: ApiChartLayout.VERTICAL,
                noLegend: true,
              },
            }}
          />
        </Box>
      </Stack>

      <Box
        sx={{
          height: table5Rows,
          flexShrink: 0,
          width: "100%",
          minWidth: 0,
          overflowX: "auto",
        }}
      >
        <StandardAnalyticsCard
          chartKey={CHART_KEY_DASHBOARD_SLOW_PAGES_TABLE}
          staticAttributes={attributes}
          ignoreAttrModel
          chart={{
            name: "Content to Improve — Speed",
            dimension: [ApiDimension.DIMENSION_LOCATION],
            template: ApiChartTemplate.TABLE,
            type: ApiChartType.TABLE,
            attributes: [],
            orderBy: `${ApiMetric.METRIC_AVERAGE_CWV_PERFORMANCE_SCORE} ASC`,
            having: [
              {
                metric: ApiMetric.METRIC_PAGEVIEWS_COUNT,
                operator: ApiOperator.IS_GREATER_THAN,
                value: 0,
              },
              {
                metric: ApiMetric.METRIC_AVERAGE_CWV_PERFORMANCE_SCORE,
                operator: ApiOperator.IS_GREATER_THAN,
                value: 0,
              },
            ],
            yAxisKeys: [
              {
                key: "l",
                fields: [
                  ApiMetric.METRIC_PAGEVIEWS_COUNT,
                  ApiMetric.METRIC_AVERAGE_CWV_PERFORMANCE_SCORE,
                  ApiMetric.METRIC_BOUNCE_RATE,
                  ApiMetric.METRIC_EXIT_RATE,
                  ApiMetric.METRIC_EFFECTIVE_PAGEVIEW_RATE,
                  ApiMetric.METRIC_REVENUE,
                ],
              },
            ],
            displayOptions: {
              noFilters: true,
              noAttributionToggle: true,
              rowsPerPage: 0,
              showTotals: true,
            },
          }}
        />
      </Box>
    </>
  );
}

export default SlowPagesTab;
